import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useBuilding } from "../../context/BuildingContext";
import { unitsApi } from "../../services/api";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";
import EmptyState from "../../components/common/EmptyState";
import SearchInput from "../../components/common/SearchInput";
import PageHeader from "../../components/common/PageHeader";
import { UnitResident, Unit, UnitStatus } from "@akfdev-com/shared/src/models";

interface UnitFilters {
  search: string;
  status: string;
  floor: string;
}

const UnitsList: React.FC = () => {
  const [units, setUnits] = useState<Unit[]>([]);
  const [filteredUnits, setFilteredUnits] = useState<Unit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<UnitFilters>({
    search: "",
    status: "",
    floor: "",
  });
  const [floors, setFloors] = useState<string[]>([]);

  const { currentBuilding } = useBuilding();

  // Status options for filtering
  const statusOptions = [
    { value: "", label: "All Statuses" },
    { value: "occupied", label: "Occupied" },
    { value: "vacant", label: "Vacant" },
    { value: "pending", label: "Pending" },
  ];

  useEffect(() => {
    const fetchUnits = async () => {
      if (!currentBuilding?.id) return;

      try {
        setLoading(true);
        const data = await unitsApi.getAll(currentBuilding.id);
        const items: Unit[] = Array.isArray(data) ? data : [];
        setUnits(items);
        setFilteredUnits(items);
        const uniqueFloors = Array.from(
          new Set(items.map((unit) => unit.floor).filter(Boolean)),
        ) as string[];
        setFloors(uniqueFloors);
      } catch (err) {
        console.error("Error fetching units:", err);
        setError("Failed to load units. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchUnits();
  }, [currentBuilding?.id]);

  useEffect(() => {
    if (units.length > 0) {
      let result = [...units];

      // Apply search filter
      if (filters.search) {
        const searchTerm = filters.search.toLowerCase();
        result = result.filter(
          (unit) =>
            unit.name.toLowerCase().includes(searchTerm) ||
            unit.residents.some((resident: UnitResident) => {
              return (
                (resident.name?.toLowerCase() || "").includes(searchTerm) ||
                (resident.email?.toLowerCase() || "").includes(searchTerm) ||
                (resident.id?.toLowerCase() || "").includes(searchTerm) ||
                (typeof resident.isPrimary === "boolean" &&
                  (resident.isPrimary ? "primary" : "secondary").includes(
                    searchTerm,
                  ))
              );
            }),
        );
      }

      // Apply status filter
      if (filters.status) {
        result = result.filter((unit) => unit.status === filters.status);
      }

      // Apply floor filter
      if (filters.floor) {
        result = result.filter((unit) => unit.floor === filters.floor);
      }

      setFilteredUnits(result);
    }
  }, [units, filters]);

  const handleFilterChange = (name: string, value: string) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: "",
      status: "",
      floor: "",
    });
  };

  // Calculate summary data
  const calculateSummary = () => {
    const total = filteredUnits.length;
    const occupied = filteredUnits.filter(
      (u) => u.status === "occupied",
    ).length;
    const vacant = filteredUnits.filter((u) => u.status === "vacant").length;
    const pending = filteredUnits.filter((u) => u.status === "pending").length;
    const totalSize = filteredUnits.reduce(
      (sum, unit) => sum + (unit.size || 0),
      0,
    );
    const averageSize = total > 0 ? totalSize / total : 0;
    const occupancyRate = total > 0 ? (occupied / total) * 100 : 0;

    return {
      total,
      occupied,
      vacant,
      pending,
      totalSize,
      averageSize,
      occupancyRate,
    };
  };

  const summary = calculateSummary();

  if (loading) {
    return <Spinner message="Loading units..." />;
  }

  if (error) {
    return <ErrorAlert message={error} />;
  }

  return (
    <div className="">
      <PageHeader
        title="Units"
        description={`Manage units for ${
          currentBuilding?.name || "your building"
        }`}
        actions={
          <Link
            to={`/dashboard/units/new${
              currentBuilding?.id ? `?building=${currentBuilding.id}` : ""
            }`}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Add Unit
          </Link>
        }
      />

      {/* Filters */}
      <div className="mt-6 bg-white shadow px-4 py-5 sm:rounded-lg sm:p-6">
        <div className="md:grid md:grid-cols-4 md:gap-6">
          <div className="md:col-span-1">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Filters
            </h3>
            <p className="mt-1 text-sm text-gray-500">
              Filter units by name, status, or floor.
            </p>
          </div>
          <div className="mt-5 md:mt-0 md:col-span-3">
            <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
              <div className="sm:col-span-6">
                <SearchInput
                  placeholder="Search by unit name or resident..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  onClear={() => handleFilterChange("search", "")}
                />
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="status"
                  className="block text-sm font-medium text-gray-700"
                >
                  Status
                </label>
                <select
                  id="status"
                  name="status"
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                  value={filters.status}
                  onChange={(e) => handleFilterChange("status", e.target.value)}
                >
                  {statusOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-3">
                <label
                  htmlFor="floor"
                  className="block text-sm font-medium text-gray-700"
                >
                  Floor
                </label>
                <select
                  id="floor"
                  name="floor"
                  className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm rounded-md"
                  value={filters.floor}
                  onChange={(e) => handleFilterChange("floor", e.target.value)}
                >
                  <option value="">All Floors</option>
                  {floors.map((floor) => (
                    <option key={floor} value={floor}>
                      {floor}
                    </option>
                  ))}
                </select>
              </div>

              <div className="sm:col-span-6 flex justify-end">
                <button
                  type="button"
                  onClick={handleClearFilters}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Summary */}
      <div className="mt-8 bg-white shadow overflow-hidden sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-gray-900">
            Summary
          </h3>
          <div className="mt-5 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Total Units
                </dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">
                  {summary.total}
                </dd>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Occupancy Rate
                </dt>
                <dd className="mt-1 text-3xl font-semibold text-green-600">
                  {Math.round(summary.occupancyRate)}%
                </dd>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Average Size
                </dt>
                <dd className="mt-1 text-3xl font-semibold text-gray-900">
                  {Math.round(summary.averageSize)} m²
                </dd>
              </div>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Vacant Units
                </dt>
                <dd className="mt-1 text-3xl font-semibold text-yellow-600">
                  {summary.vacant}
                </dd>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Units List */}
      <div className="mt-8">
        {filteredUnits.length === 0 ? (
          <EmptyState
            title="No units found"
            description={
              filters.search || filters.status || filters.floor
                ? "No units match your search criteria."
                : "You don't have any units yet."
            }
            actionText={
              filters.search || filters.status || filters.floor
                ? "Clear filters"
                : "Add your first unit"
            }
            actionHref={
              filters.search || filters.status || filters.floor
                ? undefined
                : `/dashboard/units/new${
                    currentBuilding?.id ? `?building=${currentBuilding.id}` : ""
                  }`
            }
            onAction={
              filters.search || filters.status || filters.floor
                ? handleClearFilters
                : undefined
            }
            icon={
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
            }
          />
        ) : (
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            <ul className="divide-y divide-gray-200">
              {filteredUnits.map((unit) => (
                <li key={unit.id}>
                  <Link
                    to={`/dashboard/units/${unit.id}`}
                    className="block hover:bg-gray-50"
                  >
                    <div className="px-4 py-4 sm:px-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <svg
                              className="h-8 w-8 text-gray-400"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                              />
                            </svg>
                          </div>
                          <div className="ml-4">
                            <div className="flex items-center">
                              <p className="text-sm font-medium text-primary-600 truncate">
                                {unit.name}
                              </p>
                              <div className="ml-2 flex-shrink-0 flex">
                                <p
                                  className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full 
                                  ${
                                    unit.status === "occupied"
                                      ? "bg-green-100 text-green-800"
                                      : unit.status === "pending"
                                      ? "bg-yellow-100 text-yellow-800"
                                      : "bg-gray-100 text-gray-800"
                                  }`}
                                >
                                  {unit.status.charAt(0).toUpperCase() +
                                    unit.status.slice(1)}
                                </p>
                              </div>
                            </div>
                            <div className="mt-1 text-sm text-gray-500 flex flex-wrap gap-x-4">
                              {unit.floor && (
                                <span className="flex items-center">
                                  <svg
                                    className="h-4 w-4 mr-1"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                                    />
                                  </svg>
                                  {unit.floor} Floor
                                </span>
                              )}
                              {unit.size && (
                                <span className="flex items-center">
                                  <svg
                                    className="h-4 w-4 mr-1"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5v-4m0 4h-4m4 0l-5-5"
                                    />
                                  </svg>
                                  {unit.size} m²
                                </span>
                              )}
                              {unit.bedrooms && (
                                <span className="flex items-center">
                                  <svg
                                    className="h-4 w-4 mr-1"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                                    />
                                  </svg>
                                  {unit.bedrooms}{" "}
                                  {unit.bedrooms === 1 ? "Bedroom" : "Bedrooms"}
                                </span>
                              )}
                              {unit.bathrooms && (
                                <span className="flex items-center">
                                  <svg
                                    className="h-4 w-4 mr-1"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                                    />
                                  </svg>
                                  {unit.bathrooms}{" "}
                                  {unit.bathrooms === 1
                                    ? "Bathroom"
                                    : "Bathrooms"}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="ml-2 flex flex-col flex-shrink-0 items-end">
                          <p className="text-sm font-medium text-gray-900">
                            {unit.shareNumerator}/{unit.shareDenominator} share
                          </p>
                          <div className="mt-1 text-sm text-gray-500">
                            {unit.residents.filter(
                              (r: UnitResident) => !r.moveOutDate,
                            ).length > 0 ? (
                              <>
                                {unit.residents
                                  .filter((r: UnitResident) => !r.moveOutDate)
                                  .map((resident: UnitResident) => (
                                    <span key={resident.id} className="block">
                                      {resident.name} ({resident.email})
                                      {resident.isPrimary && (
                                        <span className="ml-1 text-xs text-primary-600">
                                          Primary
                                        </span>
                                      )}
                                    </span>
                                  ))}
                              </>
                            ) : (
                              "No residents"
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
};

export default UnitsList;
